import {
  faArrowRight,
  faCheck,
  faEnvelope,
  faEye,
  faEyeSlash,
  faLock,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { observer } from "mobx-react-lite";
import { FormEvent, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { ErrorAlert } from "../shared/components/alert/Alert";
import ErrorBoundary from "../shared/components/error-boundary/ErrorBoundary";
import { LoadingEllipsis } from "../shared/components/loading/Loading";
import { useAppContext } from "../shared/functions/Context";
import DisabledAccount from "./DisabledAccount";
import "./LoggedOut.scss";

const loaderStyle = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "100vh",
  background: "#eef5ff",
};

const Loader = () => (
  <div style={loaderStyle}>
    <LoadingEllipsis />
  </div>
);

type ILocationState = {
  from: string;
};

const LoggedOut: React.FC = observer(() => {
  const { api, store } = useAppContext();
  const location = useLocation();

  const [signInForm, setSignInForm] = useState({ email: "", password: "" });
  const [resetEmail, setResetEmail] = useState("");
  const [loggingLoading, setLoggingLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [passwordType, setPasswordType] = useState<"password" | "text">(
    "password"
  );
  const [isForgotPasswordMode, setIsForgotPasswordMode] = useState(false);

  const togglePassword = () => {
    setPasswordType((previous) =>
      previous === "password" ? "text" : "password"
    );
  };

  const onSignIn = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoggingLoading(true);
    setErrorMessage("");

    const user = await api.auth.signIn(
      signInForm.email.trim(),
      signInForm.password
    );

    if (!user) {
      setErrorMessage(
        "We could not sign you in. Check your email and password, then try again."
      );
    }
    setLoggingLoading(false);
  };

  const handleResetPassword = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!resetEmail.trim()) return;

    setResetLoading(true);
    setResetMessage("");
    const sent = await api.auth.sendResetPasswordEmail(resetEmail.trim());
    setResetMessage(
      sent
        ? "If an account exists for that email, a reset link has been sent."
        : "We could not send the reset link. Please check the email and try again."
    );
    setResetLoading(false);
  };

  if (store.auth.loading) return <Loader />;

  // Older records sometimes contain the string "false". Only a real boolean true
  // should block an otherwise active account.
  if (store.auth.me?.asJson.disabled === true) {
    return (
      <DisabledAccount title="Account access paused">
        <button className="account-signout" onClick={() => api.auth.logOut()}>
          Sign out and use another account
        </button>
      </DisabledAccount>
    );
  }

  if (store.auth.me) {
    const state = location.state as ILocationState;
    return <Navigate to={state?.from ?? "/c/home/dashboard"} />;
  }

  return (
    <ErrorBoundary>
      <main className="login-container">
        <div className="login-orb login-orb-one" aria-hidden="true" />
        <div className="login-orb login-orb-two" aria-hidden="true" />

        <section className="login-card" aria-label="E-Performance login">
          <div className="login-left">
            <div className="brand-mark" aria-hidden="true">
              <span>NTC</span>
            </div>
            <p className="eyebrow">Nkurenkuru Town Council</p>
            <h1>Performance, made clear.</h1>
            <p className="brand-copy">
              One secure workspace for goals, scorecards, reviews and progress
              across the council.
            </p>

            <div className="feature-list" aria-label="Platform benefits">
              <span>
                <FontAwesomeIcon icon={faCheck} /> Track performance in one place
              </span>
              <span>
                <FontAwesomeIcon icon={faCheck} /> Keep teams aligned and accountable
              </span>
              <span>
                <FontAwesomeIcon icon={faCheck} /> Turn progress into better decisions
              </span>
            </div>

            <p className="secure-note">
              <FontAwesomeIcon icon={faLock} /> Secure council staff access
            </p>
          </div>

          <div className="login-right">
            <div className="form-heading">
              <p className="eyebrow">
                {isForgotPasswordMode ? "Account recovery" : "Welcome back"}
              </p>
              <h2>
                {isForgotPasswordMode ? "Reset your password" : "Sign in to continue"}
              </h2>
              <p>
                {isForgotPasswordMode
                  ? "Enter the email linked to your account and we’ll send a reset link."
                  : "Use your council account details to access your workspace."}
              </p>
            </div>

            {errorMessage && (
              <ErrorAlert msg={errorMessage} onClose={() => setErrorMessage("")} />
            )}

            {!isForgotPasswordMode ? (
              <form onSubmit={onSignIn}>
                <div className="input-group">
                  <label htmlFor="login-email">Email address</label>
                  <div className="input-wrapper">
                    <FontAwesomeIcon icon={faEnvelope} className="input-icon" />
                    <input
                      id="login-email"
                      type="email"
                      autoComplete="email"
                      placeholder="name@nkurenkurutc.org.na"
                      value={signInForm.email}
                      onChange={(e) =>
                        setSignInForm({ ...signInForm, email: e.target.value })
                      }
                      required
                    />
                  </div>
                </div>

                <div className="input-group">
                  <div className="label-row">
                    <label htmlFor="login-password">Password</label>
                    <button
                      type="button"
                      className="text-button"
                      onClick={() => {
                        setResetEmail(signInForm.email);
                        setResetMessage("");
                        setIsForgotPasswordMode(true);
                      }}
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div className="input-wrapper">
                    <FontAwesomeIcon icon={faLock} className="input-icon" />
                    <input
                      id="login-password"
                      type={passwordType}
                      autoComplete="current-password"
                      placeholder="Enter your password"
                      value={signInForm.password}
                      onChange={(e) =>
                        setSignInForm({ ...signInForm, password: e.target.value })
                      }
                      required
                    />
                    <button
                      type="button"
                      className="toggle-password"
                      onClick={togglePassword}
                      aria-label={
                        passwordType === "password" ? "Show password" : "Hide password"
                      }
                    >
                      <FontAwesomeIcon
                        icon={passwordType === "password" ? faEye : faEyeSlash}
                      />
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  className="login-button"
                  disabled={loggingLoading}
                >
                  <span>{loggingLoading ? "Signing in…" : "Sign in"}</span>
                  {loggingLoading ? (
                    <span className="loading-spinner" aria-hidden="true" />
                  ) : (
                    <FontAwesomeIcon icon={faArrowRight} />
                  )}
                </button>
              </form>
            ) : (
              <form onSubmit={handleResetPassword}>
                <div className="input-group">
                  <label htmlFor="reset-email">Email address</label>
                  <div className="input-wrapper">
                    <FontAwesomeIcon icon={faEnvelope} className="input-icon" />
                    <input
                      id="reset-email"
                      type="email"
                      autoComplete="email"
                      placeholder="name@nkurenkurutc.org.na"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      required
                    />
                  </div>
                </div>

                {resetMessage && (
                  <p className="form-message" role="status">
                    {resetMessage}
                  </p>
                )}

                <button
                  type="submit"
                  className="login-button"
                  disabled={resetLoading}
                >
                  <span>{resetLoading ? "Sending…" : "Send reset link"}</span>
                  {resetLoading ? (
                    <span className="loading-spinner" aria-hidden="true" />
                  ) : (
                    <FontAwesomeIcon icon={faArrowRight} />
                  )}
                </button>
                <button
                  type="button"
                  className="back-button"
                  onClick={() => {
                    setResetMessage("");
                    setIsForgotPasswordMode(false);
                  }}
                >
                  Back to sign in
                </button>
              </form>
            )}

            <p className="support-copy">
              Having trouble? Contact your system administrator.
            </p>
          </div>
        </section>
      </main>
    </ErrorBoundary>
  );
});

export default LoggedOut;
